import { useContext, useState } from 'react';

import * as S from './ProductInfoPurchase.sc';

import { Context } from '@context/PreferenceContext';
import { Availability, AvailabilityStatus } from '@utils/getAvailability';
import { Store } from '@utils/getStoreInfo';

import Button from '@components/Button/Button';
import CountrySelect, {
  countries,
} from '@components/CountrySelect/CountrySelect';
import Flag from '@components/Flag/Flag';
import Modal from '@components/Modal/Modal';

//import logos from './Logos';

const deeplinks = require('/data/deeplinks.json');

type StoreName = 'Amazon' | 'eBay' | 'LEGO Store';

type ConfigureStoreLinks = {
  brand: string;
  code: string;
  name: string;
  reference: string;
  stores: Array<StoreName>;
};

// replace keywords in the deeplink with these references
const configureStoreLinks = ({
  brand,
  code,
  name,
  reference,
  stores,
}: ConfigureStoreLinks) => {
  return stores.reduce((storeList, store: StoreName) => {
    const storeLinks = deeplinks[store];

    storeList[store] = Object.keys(storeLinks).reduce(
      (storeLinkResult, countryCode) => {
        const link = storeLinks[countryCode];

        storeLinkResult[countryCode] = link
          .replace(/\{brand\}/gi, brand)
          .replace(/\{code\}/gi, code)
          .replace(/\{name\}/gi, name)
          .replace(/\{ref\}/gi, reference);

        return storeLinkResult;
      },
      {}
    );

    return storeList;
  }, {});
};

// determine which stores to show based on the status of the product and the brand
const getStores = (status: AvailabilityStatus, brand: string) => {
  const stores: Array<StoreName> = [];

  if (status === 'Coming soon') {
    if (brand === 'LEGO') {
      stores.push('LEGO Store');
    }
  }

  if (status === 'Out now') {
    if (brand === 'LEGO') {
      stores.push('LEGO Store');
    }
    stores.push('Amazon');
  }

  if (status === 'Retired' || 'Unknown') {
    stores.push('Amazon');
    stores.push('eBay');
  }

  return stores;
};

type PopulateStoresProps = {
  brand: string;
  name: string;
  reference: string;
  status: AvailabilityStatus;
};

type StoreProps = {
  countryCode: string;
  title: string;
  url: any;
};

const populateStores = ({
  brand,
  name,
  reference,
  status,
}: PopulateStoresProps): Array<StoreProps> => {
  const storeLinks = configureStoreLinks({
    brand,
    code: name.replace(/ /gi, ''),
    name,
    reference,
    stores: getStores(status, brand),
  });

  // push everything in to one large array for consumption
  return Object.keys(storeLinks).reduce((result: StoreProps[], store) => {
    const countryLinks = storeLinks[store];

    const storeCountryLinks = Object.keys(countryLinks).reduce(
      (countries: Store[], country) => {
        countries.push({
          countryCode: country,
          title: store,
          url: countryLinks[country],
        });

        return countries;
      },
      []
    );

    return result.concat(storeCountryLinks);
  }, []);
};

export interface ProductInfoPurchaseProps {
  brand: string;
  reference?: string;
  releaseStatus: Availability;
  section: Section;
  stores: Array<Store>;
  title: string;
}

const ProductInfoPurchase = ({
  brand,
  reference,
  releaseStatus,
  section,
  stores,
  title,
}) => {
  const [showModal, setShowModal] = useState(false);
  const { changeCountry, country } = useContext(Context)!;

  const getCountry = (code: string) => {
    return (
      countries.find((country) => country.code === code) || {
        code: 'US',
        name: 'United States (default)',
      }
    );
  };

  const handleCountrySelect = (code) => {
    changeCountry(code);
    setShowModal(false);
  };

  const currentCountry = getCountry(country);

  // experimental: if no stores are listed, list our own
  if (!stores || stores.length === 0) {
    stores = populateStores({
      brand,
      name: title,
      reference,
      status: releaseStatus.status,
    });
  }

  const countryStores = stores
    .filter((store) => store.countryCode === currentCountry.code)
    .map((store) => {
      let url = store.url;

      // default store behaviour
      if (typeof store.url === 'undefined' || store.url === '') {
        url =
          deeplinks[store.title][store.countryCode || 'US'].replace(
            /\{ref\}/gi,
            reference
          ) || '';
      }

      if (!url) {
        return null;
      }

      return (
        <div key={store.title}>
          <Button
            analytics={{
              category: section,
              action: 'purchase',
              label: title,
              value: store.title,
            }}
            text={`Buy from ${store.title} online`}
            url={url}
          />
          {(process.env.ENVIRONMENT || 'local').toLowerCase() ===
            'production' && store.pixel ? (
            // eslint-disable-next-line
            <S.Pixel src={store.pixel} />
          ) : null}
        </div>
      );
    });

  return (
    <div>
      <S.Container>
        <S.Title>Buy Online</S.Title>
        <S.EditButton onClick={() => setShowModal(true)}>
          <S.CountryFlag>
            <Flag
              countryCode={currentCountry.code}
              countryName={currentCountry.name}
            />
          </S.CountryFlag>
          <S.CountryName>{currentCountry.name}</S.CountryName>
          <S.EditIcon>
            <span className="visually-hidden">Change</span>
          </S.EditIcon>
        </S.EditButton>
      </S.Container>

      <Modal
        onClose={() => setShowModal(false)}
        section={section}
        show={showModal}
        title="Select your country"
      >
        <p>
          Select your location to show <strong>purchase options</strong>{' '}
          available in your country.
        </p>
        <CountrySelect onClick={handleCountrySelect} />
        <p>
          <strong>Your country not listed?</strong> We&rsquo;re adding more
          countries all the time, so please check back!
        </p>
      </Modal>

      <S.Buttons>
        {countryStores.length > 0 ? (
          countryStores
        ) : (
          <p>We do not currently have any stores listed for your country.</p>
        )}
      </S.Buttons>
    </div>
  );
};

export default ProductInfoPurchase;
